import Phaser from 'phaser';

// PuddleReflection — the single-frame "shot" that carries the tone (Order 16.2):
// an emissive source (the marquee) reflected in a puddle on the alley floor. The
// recipe, verbatim from the plan:
//   flip the emissive vertically below the reflection plane, blur it, drop its
//   opacity, mask it to the puddle shape, and add a gentle ripple displacement so
//   the reflection wobbles.
//
// Built from three stacked Game Objects at low depth (under the Actor):
//   1. base   — the wet puddle surface (a dark elliptical sheen).
//   2. refl   — the flipped, blurred, faded, displaced emissive, masked to the
//               puddle ellipse. This is the reflection proper.
//   3. glint  — a small additive highlight so the puddle's near lip catches light.
// update(t) drives the ripple wobble (animating the Displacement filter) and a
// faint shimmer, kept loosely in phase with the marquee's flicker.

export interface PuddleConfig {
  scene: Phaser.Scene;
  /** Texture key of the emissive being reflected (the marquee). */
  sourceTexture: string;
  /** Displacement (ripple) texture key. */
  rippleTexture: string;
  /** Puddle centre, world px. */
  cx: number;
  cy: number;
  /** Puddle ellipse size, world px. */
  width: number;
  height: number;
  /** Render depth for the whole puddle group (Actor is depth 10). */
  depth?: number;
}

export class PuddleReflection {
  private base: Phaser.GameObjects.Ellipse;
  private rim: Phaser.GameObjects.Ellipse;
  private refl: Phaser.GameObjects.Image;
  private glint: Phaser.GameObjects.Ellipse;
  private disp!: Phaser.Filters.Displacement;
  private baseDispX = 0.012;
  private baseDispY = 0.02;
  private baseAlpha = 0.5;

  constructor(cfg: PuddleConfig) {
    const { scene, cx, cy, width, height } = cfg;
    const depth = cfg.depth ?? 3;

    // 1 — wet surface: a dark ellipse, faintly cool, with a lighter rim so the
    // puddle reads as a hole of water in the ground, not a painted decal.
    // Semi-transparent: the alley floor beneath is now a photographic plate, and
    // an opaque ellipse on it reads as a decal rather than as standing water.
    this.base = scene.add.ellipse(cx, cy, width, height, 0x0c1018, 0.55).setDepth(depth);
    this.rim = scene.add
      .ellipse(cx, cy, width + 4, height + 4)
      .setStrokeStyle(1, 0x4684b2, 0.35)
      .setDepth(depth);

    // 2 — the reflection: the emissive, mirrored, foreshortened into the puddle,
    // and clipped to its ellipse. Additive + low alpha so it reads as light on a
    // wet surface rather than a pasted sprite.
    //
    // The clip is BAKED INTO THE TEXTURE'S ALPHA rather than applied as a runtime
    // mask. `GameObject.setMask()` is a no-op under Phaser 4's WebGL renderer —
    // it only warns ("not supported in WebGL. Create a Mask filter instead") —
    // so the shipped reflection was an unclipped rectangle glowing 6px past the
    // puddle's bottom lip onto dry ground. Baking the ellipse into the alpha
    // needs no mask, cannot be silently dropped, and lets the reflection carry a
    // depth fade for free.
    this.refl = scene.add
      .image(cx, cy, bakeReflectionTexture(cfg))
      .setAlpha(this.baseAlpha)
      .setBlendMode(Phaser.BlendModes.ADD)
      .setDepth(depth + 1);

    // Ripple displacement, per the recipe. The recipe's BLUR is baked into the
    // texture (above) rather than added here as a runtime filter, for two reasons
    // found by looking at it: a Blur filter spreads alpha to the edges of the
    // object's quad, so the filter target composited a faint black RECTANGLE over
    // the alley floor around the ellipse; and at this size (132×28) it smeared the
    // sign into an unreadable smudge. Baked, the reflection is legibly the marquee
    // — bulb dots, red stripe and all — softened like water.
    this.refl.enableFilters();
    const internal = this.refl.filters!.internal;
    this.disp = internal.addDisplacement(cfg.rippleTexture, this.baseDispX, this.baseDispY);

    // 3 — a small near-lip glint for sparkle.
    this.glint = scene.add
      .ellipse(cx - width * 0.18, cy + height * 0.12, width * 0.3, height * 0.28, 0xffe6a8, 0.22)
      .setBlendMode(Phaser.BlendModes.ADD)
      .setDepth(depth + 2);
  }

  /** Drive the ripple wobble + shimmer. `timeSec` is scene time in seconds. */
  update(timeSec: number): void {
    // Two out-of-phase sines wobble the displacement magnitude → the reflection
    // shivers as if the water surface is moving. Gentle, never fully still.
    const wx = 0.65 + 0.35 * Math.sin(timeSec * 2.1);
    const wy = 0.65 + 0.35 * Math.sin(timeSec * 1.7 + 1.3);
    this.disp.x = this.baseDispX * wx;
    this.disp.y = this.baseDispY * wy;
    // A faint alpha shimmer keyed near the marquee flicker rate.
    this.refl.setAlpha(this.baseAlpha * (0.86 + 0.14 * Math.sin(timeSec * 6.2)));
    this.glint.setAlpha(0.18 + 0.08 * Math.sin(timeSec * 3.1 + 0.7));
  }

  destroy(): void {
    this.base.destroy();
    this.rim.destroy();
    this.refl.destroy();
    this.glint.destroy();
  }
}

/** Bake the mirrored, foreshortened, ellipse-clipped reflection into its own
 *  texture, and return its key. Cached per (source, size) so two puddles sharing
 *  a marquee bake once.
 *
 *  Three things happen here that together make it read as water rather than a
 *  flipped decal:
 *    • MIRROR — the source is drawn flipped in Y, so the edge of the sign nearest
 *      the reflection plane lands nearest the puddle's near lip.
 *    • FORESHORTEN — squashed to the puddle's height. A reflection seen at a
 *      grazing angle is compressed, not a 1:1 copy.
 *    • CLIP + FADE — an elliptical alpha, feathered at the rim, multiplied by a
 *      vertical falloff so the reflection dies away from the plane.
 */
/** How much the water softens the reflection, px at puddle resolution. Enough to
 *  read as wet, little enough that the sign stays a sign. */
const REFLECTION_BLUR_PX = 0.9;

function bakeReflectionTexture(cfg: PuddleConfig): string {
  const { scene, sourceTexture, width, height } = cfg;
  const key = `puddle-refl:${sourceTexture}:${width}x${height}`;
  if (scene.textures.exists(key)) return key;

  const src = scene.textures.get(sourceTexture).getSourceImage() as CanvasImageSource;

  const canvas = document.createElement('canvas');
  canvas.width = Math.max(1, Math.round(width));
  canvas.height = Math.max(1, Math.round(height));
  const ctx = canvas.getContext('2d')!;
  const W = canvas.width;
  const H = canvas.height;

  // Mirror + foreshorten: flip Y about the canvas centre, then fill it. The
  // recipe's softening happens HERE, as a canvas blur on the source, so no
  // runtime Blur filter is needed (see the note at the call site).
  ctx.save();
  ctx.filter = `blur(${REFLECTION_BLUR_PX}px)`;
  ctx.translate(0, H);
  ctx.scale(1, -1);
  ctx.drawImage(src, 0, 0, W, H);
  ctx.restore();

  // Depth fade: brightest at the near lip (top of the puddle, the reflection
  // plane), dying toward the far edge.
  const fade = ctx.createLinearGradient(0, 0, 0, H);
  fade.addColorStop(0, 'rgba(0,0,0,0)');
  fade.addColorStop(1, 'rgba(0,0,0,0.75)');
  ctx.globalCompositeOperation = 'destination-out';
  ctx.fillStyle = fade;
  ctx.fillRect(0, 0, W, H);

  // Elliptical clip, feathered so the rim doesn't alias into a hard cut.
  const clip = document.createElement('canvas');
  clip.width = W;
  clip.height = H;
  const cctx = clip.getContext('2d')!;
  cctx.fillStyle = '#fff';
  cctx.beginPath();
  cctx.ellipse(W / 2, H / 2, W / 2, H / 2, 0, 0, Math.PI * 2);
  cctx.fill();
  cctx.filter = 'blur(1.5px)';
  cctx.drawImage(clip, 0, 0);

  ctx.globalCompositeOperation = 'destination-in';
  ctx.drawImage(clip, 0, 0);
  ctx.globalCompositeOperation = 'source-over';

  scene.textures.addCanvas(key, canvas);
  return key;
}
